import { supabase } from '@/integrations/supabase/client';

export interface PatternBackup {
  id: string;
  file_id: string;
  pattern: string;
  frequency: number;
  next_win_count: number;
  next_loss_count: number;
  success_rate: number;
  confidence_score: number;
  backup_session_id?: string;
  backup_reason?: string;
  created_at: string;
}

export interface CurrentPattern {
  id: string;
  file_id: string;
  pattern: string;
  frequency: number;
  next_win_count: number;
  next_loss_count: number;
  success_rate: number;
  confidence_score: number;
  analysis_mode: string;
  updated_at: string;
}

export interface HonestBacktestPattern {
  id: string;
  simulation_id: string;
  file_id: string;
  pattern: string;
  frequency: number;
  next_win_count: number;
  next_loss_count: number;
  success_rate: number;
  confidence_score: number;
  position_created: number;
  position_last_updated: number;
  pattern_length: number;
  created_at: string;
}

export interface PatternComparison {
  pattern: string;
  status: 'unchanged' | 'updated' | 'created' | 'deleted';
  before?: PatternBackup | CurrentPattern | HonestBacktestPattern;
  after?: CurrentPattern | HonestBacktestPattern;
  patternLength?: number;
  changes?: {
    frequency?: { before: number; after: number };
    success_rate?: { before: number; after: number };
    confidence_score?: { before: number; after: number };
    next_win_count?: { before: number; after: number };
    next_loss_count?: { before: number; after: number };
  };
}


export const PatternComparisonService = {

  /**
   * Get backup patterns for a specific session
   */
  async getBackupPatterns(sessionId: string): Promise<{ success: boolean; data?: PatternBackup[]; error?: string }> {
    try {
      // Increased limit to handle large datasets with thousands of patterns
      const { data, error } = await supabase
        .from('pattern_backups')
        .select('*')
        .eq('backup_session_id', sessionId)
        .order('pattern')
        .limit(5000); // Support up to 5000 patterns

      if (error) {
        console.error('Error fetching backup patterns:', error);
        return { success: false, error: error.message };
      }

      console.log(`Fetched ${data?.length || 0} backup patterns for session ${sessionId}`);
      return { success: true, data: data || [] };
    } catch (error) {
      console.error('Error in getBackupPatterns:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  },

  /**
   * Get current patterns for a file
   */
  async getCurrentPatterns(fileId: string): Promise<{ success: boolean; data?: CurrentPattern[]; error?: string }> {
    try {
      // Increased limit to handle large datasets with thousands of patterns
      const { data, error } = await supabase
        .from('trading_patterns')
        .select('*')
        .eq('file_id', fileId)
        .order('pattern')
        .limit(5000); // Support up to 5000 patterns

      if (error) {
        console.error('Error fetching current patterns:', error);
        return { success: false, error: error.message };
      }

      console.log(`Fetched ${data?.length || 0} current patterns for file ${fileId}`);
      return { success: true, data: (data as CurrentPattern[]) || [] };
    } catch (error) {
      console.error('Error in getCurrentPatterns:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  },

  /**
   * Compare patterns between backup and current state
   */
  async comparePatterns(
    sessionId: string, 
    fileId: string
  ): Promise<{ success: boolean; data?: PatternComparison[]; error?: string }> {
    try {
      // First get the backup_session_id from the main session
      const { data: sessionData, error: sessionError } = await supabase
        .from('re_analysis_sessions')
        .select('backup_session_id')
        .eq('id', sessionId)
        .single();

      if (sessionError || !sessionData?.backup_session_id) {
        console.error('Session error:', sessionError);
        return { success: false, error: 'Backup session ID not found for this session' };
      }

      // Get backup patterns and current patterns
      const [backupResult, currentResult] = await Promise.all([
        this.getBackupPatterns(sessionData.backup_session_id),
        this.getCurrentPatterns(fileId)
      ]);

      if (!backupResult.success) {
        return { success: false, error: backupResult.error };
      }

      if (!currentResult.success) {
        return { success: false, error: currentResult.error };
      }

      const backupPatterns = backupResult.data || [];
      const currentPatterns = currentResult.data || [];

      // Create pattern maps for easier comparison
      const backupMap = new Map(backupPatterns.map(p => [p.pattern, p]));
      const currentMap = new Map(currentPatterns.map(p => [p.pattern, p]));

      // Get all unique pattern strings
      const allPatterns = new Set([
        ...backupPatterns.map(p => p.pattern),
        ...currentPatterns.map(p => p.pattern)
      ]);

      const comparisons: PatternComparison[] = [];

      for (const pattern of allPatterns) {
        const backup = backupMap.get(pattern);
        const current = currentMap.get(pattern);

        if (backup && current) {
          // Pattern exists in both - check for changes
          const changes: PatternComparison['changes'] = {};
          let hasChanges = false;

          if ((backup as any).frequency !== (current as any).frequency) {
            changes.frequency = { before: (backup as any).frequency, after: (current as any).frequency };
            hasChanges = true;
          }

          if ((backup as any).success_rate !== (current as any).success_rate) {
            changes.success_rate = { before: (backup as any).success_rate, after: (current as any).success_rate };
            hasChanges = true;
          }

          if ((backup as any).confidence_score !== (current as any).confidence_score) {
            changes.confidence_score = { before: (backup as any).confidence_score, after: (current as any).confidence_score };
            hasChanges = true;
          }

          if ((backup as any).next_win_count !== (current as any).next_win_count) {
            changes.next_win_count = { before: (backup as any).next_win_count, after: (current as any).next_win_count };
            hasChanges = true;
          }

          if ((backup as any).next_loss_count !== (current as any).next_loss_count) {
            changes.next_loss_count = { before: (backup as any).next_loss_count, after: (current as any).next_loss_count };
            hasChanges = true;
          }

          comparisons.push({
            pattern,
            status: hasChanges ? 'updated' : 'unchanged',
            before: backup as PatternBackup,
            after: current as CurrentPattern,
            changes: hasChanges ? changes : undefined
          });
        } else if (backup && !current) {
          // Pattern was deleted
          comparisons.push({
            pattern,
            status: 'deleted',
            before: backup as PatternBackup
          });
        } else if (!backup && current) {
          // Pattern was created
          comparisons.push({
            pattern,
            status: 'created',
            after: current as CurrentPattern
          });
        }
      }

      // Sort comparisons by status priority and pattern
      comparisons.sort((a, b) => {
        const statusOrder = { created: 1, updated: 2, unchanged: 3, deleted: 4 };
        const aOrder = statusOrder[a.status] || 5;
        const bOrder = statusOrder[b.status] || 5;
        
        if (aOrder !== bOrder) {
          return aOrder - bOrder;
        }
        
        return a.pattern.localeCompare(b.pattern);
      });

      return { success: true, data: comparisons };
    } catch (error) {
      console.error('Error in comparePatterns:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  },

  /**
   * Get comparison summary statistics using SQL-based calculations for accuracy
   */
  async getComparisonSummary(
    sessionId: string, 
    fileId: string
  ): Promise<{ 
    success: boolean; 
    data?: {
      totalBackupPatterns: number;
      totalCurrentPatterns: number;
      unchanged: number;
      updated: number;
      created: number;
      deleted: number;
    }; 
    error?: string 
  }> {
    try {
      // First get the backup_session_id from the main session
      const { data: sessionData, error: sessionError } = await supabase
        .from('re_analysis_sessions')
        .select('backup_session_id')
        .eq('id', sessionId)
        .single();

      if (sessionError || !sessionData?.backup_session_id) {
        console.error('Session error:', sessionError);
        return { success: false, error: 'Backup session ID not found for this session' };
      }

      // Get total counts first
      const [backupCountResult, currentCountResult] = await Promise.all([
        supabase
          .from('pattern_backups')
          .select('*', { count: 'exact', head: true })
          .eq('backup_session_id', sessionData.backup_session_id),
        supabase
          .from('trading_patterns')
          .select('*', { count: 'exact', head: true })
          .eq('file_id', fileId)
      ]);

      if (backupCountResult.error) {
        return { success: false, error: backupCountResult.error.message };
      }

      if (currentCountResult.error) {
        return { success: false, error: currentCountResult.error.message };
      }

      const totalBackupPatterns = backupCountResult.count || 0;
      const totalCurrentPatterns = currentCountResult.count || 0;

      // Use SQL-based calculation for accurate comparison regardless of dataset size
      const { data: comparisonResult, error: comparisonError } = await supabase.rpc('calculate_pattern_comparison', {
        p_backup_session_id: sessionData.backup_session_id,
        p_file_id: fileId
      });

      if (comparisonError) {
        console.error('Error calculating pattern comparison:', comparisonError);
        // Fallback to basic calculation if SQL function fails
        const summary = {
          totalBackupPatterns,
          totalCurrentPatterns,
          unchanged: 0,
          updated: 0,
          created: Math.max(0, totalCurrentPatterns - totalBackupPatterns),
          deleted: Math.max(0, totalBackupPatterns - totalCurrentPatterns)
        };
        return { success: true, data: summary };
      }

      // Extract results from the SQL function
      const result = comparisonResult?.[0];
      const summary = {
        totalBackupPatterns,
        totalCurrentPatterns,
        unchanged: result?.unchanged || 0,
        updated: result?.updated || 0,
        created: result?.created || 0,
        deleted: result?.deleted || 0
      };

      console.log(`Pattern comparison summary calculated: unchanged=${summary.unchanged}, updated=${summary.updated}, created=${summary.created}, deleted=${summary.deleted}`);
      return { success: true, data: summary };
    } catch (error) {
      console.error('Error in getComparisonSummary:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  },

  /**
   * Get comparison summary with lazy loading for detailed analysis
   */
  async getDetailedComparison(
    sessionId: string, 
    fileId: string,
    limit?: number,
    offset?: number
  ): Promise<{ success: boolean; data?: PatternComparison[]; error?: string; hasMore?: boolean }> {
    try {
      // Use pagination for large datasets with increased page size
      const pageSize = limit || 1000; // Increased default page size for better viewing
      const startIndex = offset || 0;

      const result = await this.comparePatterns(sessionId, fileId);
      
      if (!result.success) {
        return { success: false, error: result.error };
      }

      const allComparisons = result.data || [];
      const paginatedData = allComparisons.slice(startIndex, startIndex + pageSize);
      const hasMore = startIndex + pageSize < allComparisons.length;

      return { 
        success: true, 
        data: paginatedData,
        hasMore 
      };
    } catch (error) {
      console.error('Error in getDetailedComparison:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  },

};